import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { BreakpointTrackerContext } from '@jutro/layout';
import { TranslatorContext } from '@jutro/locale';
import { Chevron, ModalNextProvider } from '@jutro/components';
import { ClausesUtil } from 'gw-policycommon-util-js';
import { validationPropTypes, withValidation } from 'gw-portals-validation-react';
import { ViewModelUtil } from 'gw-portals-viewmodel-js';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import QuoteTableAccordionCardIterableComponent
    from 'gw-components-platform-react/QuoteTableAccordionCardIterableComponent/QuoteTableAccordionCardIterableComponent';
import { messages as platformMessages } from 'gw-platform-translations';
import SingleClauseComponentVM from '../Clauses/SingleClauseComponentVM';
// import UnderwritingIssues from 'gw-components-platform-react/UnderwritingIssues/UnderwritingIssues';
import metadata from './QuoteClauseTable.metadata.json5';
import styles from './QuoteClauseTable.module.scss';
// import messages from 'gw-components-platform-react/QuoteClauseTable/QuoteClauseTable.messages';
import messages from './QuoteClauseTable.messages';
import NonBlockingMsgPopup from './NonBlockingMsgPopup';

const BLOCKS_QUOTES = ['BlocksQuote', 'BlocksQuoteRelease'];
const BLOCKS_BIND = 'BlocksBind';
const NON_BLOCKING = 'NonBlocking';

function getChangedDataPath(changedPath, fullChangedPath, pathToChangeTo) {
    const localisedVMPath = changedPath.replace(fullChangedPath, pathToChangeTo);
    return ViewModelUtil.getNonVMPath(localisedVMPath);
}

function getChangedColumnData(changedPathOfModel, columnData) {
    // onBlur is called from Jutro with a object instead of a path
    const path = _.isString(changedPathOfModel) ? changedPathOfModel : changedPathOfModel.model;
    return columnData.find(({ lob }) => _.includes(path, lob.path));
}

function getChangedClause(path, columnData) {
    const changedObjectPath = ClausesUtil.getObjectPathFromChangedPath(path);
    const changedColumn = getChangedColumnData(path, columnData);
    const changedPath = getChangedDataPath(changedObjectPath, changedColumn.lob.path, 'lob.data');
    return _.get(changedColumn, changedPath);
}

function getCoverageIndex(path) {
    const hasSubCoverages = ClausesUtil.hasSubCoverages(path);

    if (hasSubCoverages) {
        const parentCoveragePath = ClausesUtil.getParentCoveragePath(path);
        if (parentCoveragePath) {
            // Regex to get index of the child element, eg. '2'  for ...children[2]
            const pathIdentifier = parentCoveragePath.split('.').slice(-1);
            const [, index] = /\[(\d+)\][^[]*$/.exec(pathIdentifier) || [];
            return index ? `__${index}` : '';
        }
    }

    return '';
}

function selectMetadata(breakpoint) {
    const contentArray = metadata.componentContent.content;
    let metadataToRender = '';

    if (breakpoint === 'phone') {
        metadataToRender = contentArray.filter((item) => item.id !== 'desktopTable');
    } else {
        metadataToRender = contentArray.filter(
            (item) => item.id !== 'phoneTable' && item.id !== 'mobileAccordionHeader'
        );
    }

    const filteredMetdata = {
        ...metadata,
        componentContent: {
            content: metadataToRender
        }
    };

    return filteredMetdata;
}

function getSelectedCoverages(data) {
    // only return the selected base coverages names
    const coveragesObject = _.get(data, 'lob.data.coverages');
    const coverageNames = Object.keys(coveragesObject).filter((name) => {
        return _.includes(name.toLowerCase(), 'coverages');
    });
    const baseCoverageName = _.first(coverageNames);
    const coverages = _.get(coveragesObject, baseCoverageName);
    return coverages.filter((cov) => cov.selected);
}

const PAYMENT_TYPES = {
    monthly: 'monthly',
    annually: 'annually'
};

const moneyPropsTypes = PropTypes.shape({
    amount: PropTypes.string,
    currency: PropTypes.number
});

class QuoteClauseTable extends Component {
    static contextType = TranslatorContext;

    static propTypes = {
        columnData: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string,
                code: PropTypes.string,
                quote: PropTypes.shape({
                    path: PropTypes.string,
                    premium: PropTypes.shape({
                        monthlyPremium: moneyPropsTypes,
                        total: moneyPropsTypes
                    })
                })
            })
        ).isRequired,
        tableData: PropTypes.arrayOf(
            PropTypes.shape({
                header: PropTypes.string,
                data: PropTypes.arrayOf(
                    PropTypes.shape({
                        publicID: PropTypes.string
                    })
                ),
                tableContent: PropTypes.arrayOf(
                    PropTypes.shape({
                        path: PropTypes.string,
                        clauses: PropTypes.arrayOf(PropTypes.shape({})),
                        code: PropTypes.string
                    })
                )
            })
        ).isRequired,
        underwritingIssues: PropTypes.arrayOf({}),
        filterUWIssuesInCustomOffering: PropTypes.bool,
        quoteID: PropTypes.string.isRequired,
        onBuyNow: PropTypes.func,
        onRecalculate: PropTypes.func,
        onResetQuote: PropTypes.func,
        onChangeSubmissionAndSync: PropTypes.func,
        onChangeSubmission: PropTypes.func,
        onSyncCoverages: PropTypes.func,
        onStaleQuoteBranchCode: PropTypes.func,
        onChangeAutoMultilineDiscount: PropTypes.func,
        onChangeMultilineDiscount: PropTypes.func,
        onChangePaymentPlanFrequency: PropTypes.func,
        baseData: PropTypes.object,
        bindData: PropTypes.object,
        ...validationPropTypes
    };

    static defaultProps = {
        onBuyNow: undefined,
        onRecalculate: undefined,
        onResetQuote: undefined,
        onChangeSubmissionAndSync: undefined,
        onChangeSubmission: undefined,
        onSyncCoverages: undefined,
        onStaleQuoteBranchCode: undefined,
        underwritingIssues: [],
        filterUWIssuesInCustomOffering: true,
        onChangeAutoMultilineDiscount: undefined,
        onChangePaymentPlanFrequency: undefined,
        onChangeMultilineDiscount: undefined
    };

    state = {
        formData: {},
        loadingClause: undefined,
        staleQuoteBranchCodes: this.getStaleCode(),
        resetBranchCodes: [],
        priceDifference: 0,
        previousColumnData: [],
        isPaymentFreqOrMultilineDiscountChanged: false,
        isSetSelectedVersionSubIsInProgress: false,
        isBinderPolicyChanged: false,
        isNoteForUWChanged: false
    };

    componentDidMount() {
        const { columnData } = this.props;
        const resetBranchCodes = columnData.map((item) => item.code);
        this.setState({ resetBranchCodes, previousColumnData: _.cloneDeep(columnData) });
    }

    getTableColumnContent(columnData, tableInfo) {
        return columnData.map((data, index) => {
            return {
                id: `quoteTableColumn${index}`,
                type: 'element',
                component: 'tablecolumn',
                componentProps: {
                    cellClassName: 'gwTableCell',
                    onCell: this.renderCellContent,
                    data: tableInfo.tableContent.find((content) => content.code === data.code)
                }
            };
        });
    }

    getStaleCode() {
        const { onStaleQuoteBranchCode } = this.props;
        let val = [];
        if (onStaleQuoteBranchCode) {
            if (onStaleQuoteBranchCode()) {
                const staleCodes = onStaleQuoteBranchCode();
                if (_.isArray(staleCodes)) {
                    val = staleCodes;
                } else {
                    val.push(staleCodes);
                }
            }
        }
        return val;
    }

    changeAutoMultilineDiscount= (value, path) => {
        let { formData, isPaymentFreqOrMultilineDiscountChanged } = this.state;

        const { onChangeAutoMultilineDiscount } = this.props;
        if (onChangeAutoMultilineDiscount) {
            onChangeAutoMultilineDiscount(value, path);
        }
        _.set(formData, path, value);
        isPaymentFreqOrMultilineDiscountChanged = true;
        this.setState({ formData, isPaymentFreqOrMultilineDiscountChanged });
    };

    changeMultilineDiscount= (value, path) => {
        let { formData, isPaymentFreqOrMultilineDiscountChanged } = this.state;

        const { onChangeMultilineDiscount } = this.props;
        if (onChangeMultilineDiscount) {
            onChangeMultilineDiscount(value, path);
        }
        _.set(formData, path, value);
        isPaymentFreqOrMultilineDiscountChanged = true;
        this.setState({ formData, isPaymentFreqOrMultilineDiscountChanged });
    };

    changeBinderPolicy= (value, path) => {
        let { formData, isBinderPolicyChanged } = this.state;

        const { onChangeBinderPolicy } = this.props;
        if (onChangeBinderPolicy) {
            onChangeBinderPolicy(value, path);
        }
        _.set(formData, path, value);
        isBinderPolicyChanged = true;
        this.setState({ formData, isBinderPolicyChanged });
    };

    changenoteForUW= (value, path) => {
        let { formData, isNoteForUWChanged } = this.state;

        const { onChangeNoteForUW } = this.props;
        if (onChangeNoteForUW) {
            onChangeNoteForUW(value, path);
        }
        _.set(formData, path, value);
        isNoteForUWChanged = true;
        this.setState({ formData, isNoteForUWChanged });
    };

    changePaymentPlanFrequency= (value, path) => {
        let { formData, isPaymentFreqOrMultilineDiscountChanged } = this.state;

        const { onChangePaymentPlanFrequency } = this.props;
        if (onChangePaymentPlanFrequency) {
            onChangePaymentPlanFrequency(value, path);
        }
        _.set(formData, path, value);
        isPaymentFreqOrMultilineDiscountChanged = true;
        this.setState({ formData, isPaymentFreqOrMultilineDiscountChanged });
    };

    getChangedOfferingPathsAndSetClauseToLoading(path) {
        const { columnData } = this.props;
        const { staleQuoteBranchCodes, resetBranchCodes } = this.state;

        const changedColumn = getChangedColumnData(path, columnData);
        const changedClause = getChangedClause(path, columnData);
        const coverageIndex = getCoverageIndex(path);
        const lobPath = changedColumn.lob.path;
        const quotePath = changedColumn.quote.path;
        const updatedBranchCodes = resetBranchCodes.filter((item) => item !== changedColumn.code);

        staleQuoteBranchCodes.push(changedColumn.code);

        this.setState({
            loadingClause: {
                clauseID: changedClause.coveragePublicID
                    || `${changedClause.publicID}${coverageIndex}`,
                quoteCode: changedColumn.code
            },
            staleQuoteBranchCodes,
            resetBranchCodes: updatedBranchCodes
        });

        return [lobPath, quotePath];
    }

    removeStaleQuote = (path) => {
        const { staleQuoteBranchCodes, resetBranchCodes } = this.state;
        const selectedColumn = _.get(this.props, path);

        const newStateQuoteArray = staleQuoteBranchCodes.filter(
            (item) => item !== selectedColumn.code
        );
        const newResetBranchArray = resetBranchCodes.filter((item) => item !== selectedColumn.code);
        this.setState({
            staleQuoteBranchCodes: newStateQuoteArray,
            resetBranchCodes: newResetBranchArray
        });
    };

    getClickedChangedPaths = (evt) => {
        const path = evt.currentTarget.attributes.path.value;
        const selectedColumn = _.get(this.props, path);
        const lobPath = selectedColumn.lob.path;
        const quotePath = selectedColumn.quote.path;

        return [path, lobPath, quotePath];
    };

    renderMobileQuoteHeader = (accordionData, index, isFormValid) => (isOpen) => {
        const {
            formData, staleQuoteBranchCodes, resetBranchCodes, priceDifference
        } = this.state;
        const { extensionUpdated, disableRecalculate } = this.props;
        const scheduleStaleCode = this.getStaleCode();
        if (_.isEmpty(staleQuoteBranchCodes) && !_.isEmpty(scheduleStaleCode)) {
            this.setState({ staleQuoteBranchCodes: scheduleStaleCode });
        }
        const isAnnually = formData.paymentToggle === PAYMENT_TYPES.annually;
        const isQuoteStale = _.includes(staleQuoteBranchCodes, accordionData.code);
        const isResetBranch = _.includes(resetBranchCodes, accordionData.code);
        const shouldShowPriceDifference = _.includes(priceDifference.columnPath, `[${index}]`)
            && !isQuoteStale
            && priceDifference.difference !== 0;
        const price = isAnnually
            ? accordionData.quote.data.premium.total
            : accordionData.quote.data.premium.monthlyPremium;

        const dataForComponent = {
            price,
            quoteName: accordionData.name,
            selectedCoverages: getSelectedCoverages(accordionData)
        };

        // Due to having a new render content, there may be duplicate ID's
        const overrides = {
            monthlyAmountText: {
                //  visible: formData.paymentToggle === PAYMENT_TYPES.monthly
                visible: true,
            },
            annuallyAmountText: {
                // visible: formData.paymentToggle === PAYMENT_TYPES.annually
                visible: true
            },
            buyNowButton: {
                path: `columnData[${index}]`,
                visible: !isQuoteStale && !extensionUpdated,
                disabled: !isFormValid,
                onClick: (event) => {
                    event.stopPropagation();
                    this.buyNow(accordionData.quote.path, accordionData.lob.path);
                }
            },
            recalculateButton: {
                path: `columnData[${index}]`,
                visible: isQuoteStale || !!extensionUpdated,
                disabled: !isFormValid || disableRecalculate,
                onClick: (event) => {
                    event.stopPropagation();
                    this.recalculate(
                        `columnData[${index}]`,
                        accordionData.quote.path,
                        accordionData.lob.path
                    );
                }
            },
            resetCoverages: {
                path: `columnData[${index}]`,
                visible: !isResetBranch || isQuoteStale || shouldShowPriceDifference,
                onClick: (event) => {
                    event.stopPropagation();
                    this.resetQuote(
                        `columnData[${index}]`,
                        accordionData.quote.path,
                        accordionData.lob.path
                    );
                }
            },
            selectedCoverages: {
                visible: !isOpen
            },
            moreInfoContainer: {
                visible: !isOpen
            },
            accordionChevron: {
                isOpen
            },
            accordionStickyHeader: {
                className: classNames(styles.accordionStickyHeader, {
                    [styles.mobileStickyHeader]: isOpen
                })
            }
        };

        const resolvers = {
            resolveClassNameMap: styles
        };

        const metadataToRender = metadata.contentForMobileView.content;
        return (
            <ViewModelForm
                uiProps={metadataToRender}
                model={dataForComponent}
                overrideProps={overrides}
                classNameMap={resolvers.resolveClassNameMap}
            />
        );
    };

    renderCellContent = (clauseCellUniqueID, index, props) => {
        const { loadingClause } = this.state;
        const { onChangeSubmission, onValidate } = this.props;
        const { path, clauses = [], code } = props.data;
        const clauseIndex = clauses.findIndex(
            (clause) => clauseCellUniqueID.coverageUniqueID.split('__')[0] === clause.publicID
        );
        const clause = clauses[clauseIndex];
        const isQuoteLoading = _.get(loadingClause, 'quoteCode') === code && _.isUndefined(loadingClause.clauseID);
        const isClauseLoading = _.get(loadingClause, 'clauseID') === clauseCellUniqueID.coverageUniqueID
            && _.get(loadingClause, 'quoteCode') === code;

        if (clauseIndex !== -1) {
            return (
                <SingleClauseComponentVM
                    value={clause}
                    path={`${path}.children[${clauseIndex}]`}
                    onChangeClause={onChangeSubmission}
                    onSyncCoverages={this.syncCoverages}
                    onChangeSubmissionAndSync={this.changeSubmissionAndSync}
                    loadingClause={isClauseLoading || isQuoteLoading}
                    idPrefex={code}
                    labelTop
                    labelPosition="top"
                    onValidate={onValidate}
                    showAmount={false}
                />
            );
        }
        return null;
    };

    writeValue = (value, path) => {
        const { formData } = this.state;
        _.set(formData, path, value);
        this.setState({ formData });
    };

    syncCoverages = (value, path) => {
        const { onSyncCoverages } = this.props;

        const [lobPath, quotePath] = this.getChangedOfferingPathsAndSetClauseToLoading(path);

        if (onSyncCoverages) {
            onSyncCoverages(value, path, lobPath, quotePath).then(() => {
                this.setState({ loadingClause: undefined });
            });
        }
    };

    changeSubmissionAndSync = (value, path) => {
        const { onChangeSubmissionAndSync } = this.props;

        const [lobPath, quotePath] = this.getChangedOfferingPathsAndSetClauseToLoading(path);

        if (onChangeSubmissionAndSync) {
            onChangeSubmissionAndSync(value, path, lobPath, quotePath).then(() => {
                this.setState({ loadingClause: undefined });
            });
        }
    };

     showModal = async (nonBlockingMessage) => {
         const componentProps = {
             title: messages.nonBlockingMessageTitle,
             actionBtnLabel: platformMessages.ok,
             nonBlockingMessage: nonBlockingMessage,
         };
         const results = await ModalNextProvider.showModal(
             <NonBlockingMsgPopup {...componentProps} />
         );
         return results;
     }

    buyNow = async (quotePath, lobPath) => {
        const isModalResolved = false;
        const { onBuyNow, underwritingIssues } = this.props;
        this.setState({ isSetSelectedVersionSubIsInProgress: true });

        const nonBlockingMessage = underwritingIssues.filter(
            (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING
            && uwIssue.hasApprovalOrRejection === false
        );
        const hasNonBlockingMessage = nonBlockingMessage.length > 0;

        if (hasNonBlockingMessage) {
            try {
                await this.showModal(nonBlockingMessage);
                this.isModalResolved = true;
            } catch (err) {
                console.error(err);
            }
        }

        if ((this.isModalResolved && hasNonBlockingMessage) || (!hasNonBlockingMessage)) {
            if (onBuyNow) {
                await onBuyNow(lobPath, quotePath);
                this.setState({ isSetSelectedVersionSubIsInProgress: false });
            }
        }
    };

    recalculate = (columnPath, quotePath, lobPath) => {
        const { onRecalculate, columnData, updateQuotedData } = this.props;

        const { previousColumnData, formData } = this.state;
        const changedColumnData = getChangedColumnData(lobPath, previousColumnData);
        let monthlyPayment = 0;
        if (changedColumnData.quote.data.status === 'Quoted') {
            monthlyPayment = changedColumnData.quote.data.premium.monthlyPremium.amount;
        }

        this.setState({
            loadingClause: {
                quoteCode: changedColumnData.code
            }
        });

        if (onRecalculate) {
            onRecalculate(lobPath, quotePath).then((response) => {
                updateQuotedData(true);
                if (response.noteForUW) {
                    formData.note = response.noteForUW;
                }
                if (response.quote.premium.monthlyPremium) {
                    const newMonthlyPayment = response.quote.premium.monthlyPremium.amount;
                    this.removeStaleQuote(columnPath);
                    this.setState({
                        priceDifference: {
                            columnPath: columnPath,
                            difference: newMonthlyPayment - monthlyPayment
                        },
                        previousColumnData: _.cloneDeep(columnData),
                        loadingClause: undefined,
                        isPaymentFreqOrMultilineDiscountChanged: false,
                        isBinderPolicyChanged: false,
                        isNoteForUWChanged: true
                    });
                } else if (response.quote) {
                    this.setState({
                        loadingClause: undefined
                    });
                }
            });
        }
    };

    resetQuote = (columnPath, quotePath, lobPath) => {
        const { onResetQuote, columnData } = this.props;
        const { staleQuoteBranchCodes, resetBranchCodes } = this.state;
        const changedColumnData = getChangedColumnData(lobPath, columnData);
        this.setState({
            loadingClause: {
                quoteCode: changedColumnData.code
            }
        });
        if (onResetQuote) {
            onResetQuote(lobPath, quotePath).then(() => {
                staleQuoteBranchCodes.push(changedColumnData.code);
                resetBranchCodes.push(changedColumnData.code);
                this.setState({
                    loadingClause: undefined,
                    staleQuoteBranchCodes,
                    resetBranchCodes
                });
            });
        }
    };

    isAutoPolicy = (baseData) => {
        if (baseData && baseData.productCode === 'BusinessAuto') {
            return true;
        }
        return false;
    }

    isResidentialPolicy = (baseData) => {
        if (baseData && baseData.productCode === 'HOPHomeowners') {
            return true;
        }
        return false;
    }

    getPaymentFrequencyData = (paymentPlanData) => {
        if (paymentPlanData) {
            const paymentFrequencyOptionsArray = paymentPlanData.map((key) => {
                return {
                    name: key.name,
                    code: key.billingId
                };
            });
            return paymentFrequencyOptionsArray;
        }
        return [];
    };

    getAutoMultilineDiscountData = () => {
        const autoMultilineDiscountOptionsArray = [
            {
                code: 'Residential',
                name: 'Residential'
            },
            {
                code: 'Farm',
                name: 'Farm'
            },
            {
                code: 'Commercial',
                name: 'Commercial'
            }
        ];
        return autoMultilineDiscountOptionsArray;
    };

    generateDesktopHeaderOverrides() {
        const {
            columnData, isComponentValid, underwritingIssues,
            isQuoted, updateQuotedData, extensionUpdated, disableRecalculate,
            additionalWizardData, updateAdditionalWizardData
        } = this.props;
        const {
            staleQuoteBranchCodes, resetBranchCodes, priceDifference,
            isPaymentFreqOrMultilineDiscountChanged,
            isSetSelectedVersionSubIsInProgress, isBinderPolicyChanged
        } = this.state;

        const scheduleStaleCode = this.getStaleCode();
        if (_.isEmpty(staleQuoteBranchCodes) && !_.isEmpty(scheduleStaleCode)) {
            this.setState({ staleQuoteBranchCodes: scheduleStaleCode });
        }
        const overrides = columnData.map(({ code, quote, lob }, index) => {
            const columnPath = `columnData[${index}]`;
            const isQuoteStale = _.includes(staleQuoteBranchCodes, code);
            const isResetBranch = _.includes(resetBranchCodes, code);
            const clearAmount = { amount: undefined };

            let monthlyValue = clearAmount;
            let annuallyValue = clearAmount;
            let isQuoteBlocked = false;
            if (quote.data && isQuoted) {
                const { data } = quote;
                const monthlyPremium = Object.assign({}, clearAmount, data.premium.monthlyPremium);
                const total = Object.assign({}, clearAmount, data.premium.total);
                monthlyValue = Object.assign({}, clearAmount, monthlyPremium);
                annuallyValue = Object.assign({}, clearAmount, total);
                isQuoteBlocked = underwritingIssues.some(
                    (uwIssue) => uwIssue.offering === data.branchName
                        && uwIssue.currentBlockingPoint !== NON_BLOCKING
                        && uwIssue.currentBlockingPoint !== BLOCKS_BIND
                );
            }

            let monthlyHeader = isQuoteStale ? { [`monthlyAmount${index}`]: { value: monthlyValue } } : {};
            let annuallyHeader = isQuoteStale ? { [`annuallyAmount${index}`]: { value: annuallyValue } } : {};

            monthlyHeader = !isQuoted ? {
                [`monthlyAmount${index}`]: { value: monthlyValue }
            } : monthlyHeader;
            annuallyHeader = !isQuoted ? {
                [`annuallyAmount${index}`]: { value: annuallyValue }
            } : annuallyHeader;

            const shouldShowPriceDifference = _.includes(priceDifference.columnPath, `[${index}]`)
                && !isQuoteStale
                && priceDifference.difference !== 0;


            return {
                [`quoteAnnuallyAmount${index}`]: {
                    // visible: formData.paymentToggle === PAYMENT_TYPES.annually
                    visible: true
                },
                [`quoteMonthlyAmount${index}`]: {
                    //  visible: formData.paymentToggle === PAYMENT_TYPES.monthly
                    visible: true
                },
                [`priceDifference${index}`]: {
                    visible: shouldShowPriceDifference,
                    value: priceDifference.difference
                },
                [`buyNowButton${index}`]: {
                    visible: !isQuoteStale && !extensionUpdated && quote.data.status !== 'Draft' && isQuoted && !isPaymentFreqOrMultilineDiscountChanged && !isBinderPolicyChanged,
                    onClick: () => this.buyNow(quote.path, lob.path),
                    disabled: isQuoteBlocked || isSetSelectedVersionSubIsInProgress
                    || !isComponentValid,
                },
                [`recalculateButtonFirstQuote${index}`]: {
                    visible: !isQuoteStale && (!isQuoted)
                    && !isPaymentFreqOrMultilineDiscountChanged && !extensionUpdated
                    && !isBinderPolicyChanged,
                    disabled: !isComponentValid || disableRecalculate,
                    onClick: () => {
                        if (quote.data.status === 'Draft') {
                            this.recalculate(columnPath, quote.path, lob.path);
                        } else {
                            updateQuotedData(true);
                            // eslint-disable-next-line no-underscore-dangle
                            const _additionalWizardData = _.clone(additionalWizardData);
                            _additionalWizardData.showError = true;
                            updateAdditionalWizardData(_additionalWizardData);
                        }
                    }
                },
                [`recalculateButton${index}`]: {
                    visible: isQuoteStale || isPaymentFreqOrMultilineDiscountChanged || isBinderPolicyChanged || (quote.data.status === 'Draft' && isQuoted) || !!extensionUpdated,
                    disabled: !isComponentValid || disableRecalculate,
                    onClick: () => this.recalculate(columnPath, quote.path, lob.path)
                },
                [`resetCoverages${index}`]: {
                    visible: !isResetBranch,
                    onClick: () => this.resetQuote(columnPath, quote.path, lob.path)
                },
                [`monthlyAmountText${index}`]: { visible: !isQuoteStale && isQuoted },
                [`annuallyAmountText${index}`]: { visible: !isQuoteStale && isQuoted },
                [`warningIcon${index}`]: {
                    visible: isQuoteBlocked
                },
                ...monthlyHeader,
                ...annuallyHeader
            };
        });

        return Object.assign({}, ...overrides);
    }

    generatePhoneHeaderOverrides() {
        const {
            tableData, columnData, isComponentValid, underwritingIssues, isQuoted,
            updateQuotedData, extensionUpdated, disableRecalculate,
            additionalWizardData, updateAdditionalWizardData
        } = this.props;
        const {
            staleQuoteBranchCodes, resetBranchCodes, priceDifference,
            isPaymentFreqOrMultilineDiscountChanged,
            isSetSelectedVersionSubIsInProgress, isBinderPolicyChanged
        } = this.state;

        const scheduleStaleCode = this.getStaleCode();
        if (_.isEmpty(staleQuoteBranchCodes) && !_.isEmpty(scheduleStaleCode)) {
            this.setState({ staleQuoteBranchCodes: scheduleStaleCode });
        }

        const overrides = columnData.map((data, index) => {
            const { quote, code, lob } = data;
            const columnPath = `columnData[${index}]`;
            const isQuoteStale = _.includes(staleQuoteBranchCodes, code);
            const isResetBranch = _.includes(resetBranchCodes, code);
            const clearAmount = { amount: undefined };

            let monthlyValue = clearAmount;
            let annuallyValue = clearAmount;
            let isQuoteBlocked = false;
            if (quote.data && isQuoted) {
                const { data } = quote;
                const monthlyPremium = Object.assign({}, clearAmount, data.premium.monthlyPremium);
                const total = Object.assign({}, clearAmount, data.premium.total);
                monthlyValue = Object.assign({}, clearAmount, monthlyPremium);
                annuallyValue = Object.assign({}, clearAmount, total);
                isQuoteBlocked = underwritingIssues.some(
                    (uwIssue) => uwIssue.offering === data.branchName
                        && uwIssue.currentBlockingPoint !== NON_BLOCKING
                );
            }

            let monthlyHeader = isQuoteStale
                ? { [`mobileMonthlyAmount${index}`]: { value: monthlyValue } } : {};
            let annuallyHeader = isQuoteStale
                ? { [`mobileAnnuallyAmount${index}`]: { value: annuallyValue } } : {};

            monthlyHeader = !isQuoted ? { [`mobileMonthlyAmount${index}`]: { value: monthlyValue } } : monthlyHeader;
            annuallyHeader = !isQuoted ? { [`mobileAnnuallyAmount${index}`]: { value: annuallyValue } } : annuallyHeader;

            const shouldShowPriceDifference = _.includes(priceDifference.columnPath, `[${index}]`)
                && !isQuoteStale
                && priceDifference.difference !== 0;
            return {
                [`tableContainer${index}`]: {
                    header: this.renderMobileQuoteHeader(data, 0, isComponentValid)
                },
                [`quoteTableAccordionCardIterableComponentContainer${index}`]: {
                    tableData: tableData,
                    renderCellContent: this.renderCellContent,
                    onValueChange: this.writeValue
                },
                [`mobilePriceDifference${index}`]: {
                    visible: shouldShowPriceDifference,
                    value: priceDifference.difference
                },
                [`mobileBuyNowButton${index}`]: {
                    visible: !isQuoteStale && !extensionUpdated && quote.data.status !== 'Draft' && isQuoted && !isPaymentFreqOrMultilineDiscountChanged && !isBinderPolicyChanged,
                    onClick: () => this.buyNow(quote.path, lob.path),
                    disabled: isQuoteBlocked || isSetSelectedVersionSubIsInProgress
                },
                [`mobileRecalculateButtonFirstQuote${index}`]: {
                    visible: !isQuoteStale
                    && (!isQuoted)
                    && !isPaymentFreqOrMultilineDiscountChanged
                    && !extensionUpdated
                    && !isBinderPolicyChanged,
                    disabled: !isComponentValid || disableRecalculate,
                    onClick: () => {
                        if (quote.data.status === 'Draft') {
                            this.recalculate(columnPath, quote.path, lob.path);
                        } else {
                            updateQuotedData(true);
                            // eslint-disable-next-line no-underscore-dangle
                            const _additionalWizardData = _.clone(additionalWizardData);
                            _additionalWizardData.showError = true;
                            updateAdditionalWizardData(_additionalWizardData);
                        }
                    }
                },
                [`mobileResetCoverages${index}`]: {
                    visible: !isResetBranch,
                    onClick: () => this.resetQuote(columnPath, quote.path, lob.path)
                },
                [`mobileRecalculateButton${index}`]: {
                    visible: isQuoteStale || isPaymentFreqOrMultilineDiscountChanged || isBinderPolicyChanged || (quote.data.status === 'Draft' && isQuoted) || !!extensionUpdated,
                    disabled: !isComponentValid || disableRecalculate,
                    onClick: () => this.recalculate(columnPath, quote.path, lob.path)
                },
            };
        });

        return Object.assign({}, ...overrides);
    }

    generateScheduledItemOverRide() {
        const { submissionVM, onScheduleChange } = this.props;
        const packages = _.get(submissionVM, 'lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children');
        let overrides = [];
        if (packages && packages.length > 0) {
            overrides = packages.map((_package, index) => {
                return {
                    [`scheduledItemsContainer${index}`]: {
                        visible: _package.schedules.children.length > 0
                    },
                    [`scheduleItems${index}`]: {
                        model: submissionVM,
                        content: _.get(_package, 'schedules.children').map((item, sIndex) => {
                            return {
                                id: `scheduleItemId${index}${sIndex}`,
                                type: 'field',
                                component: 'ScheduleItemsComponent',
                                componentProps: {
                                    data: submissionVM,
                                    model: submissionVM,
                                    value: item.value,
                                    dataPath: `lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children.${index}.schedules.children.${sIndex}`,
                                    onScheduleChange: onScheduleChange
                                }
                            };
                        })
                    }
                };
            });
        }

        return Object.assign({}, ...overrides);
    }

    generateDesktopTableOverrides() {
        const { tableData, columnData, submissionVM } = this.props;

        let overrides = [];

        if (columnData[0].lob.data.coverages.vehicleCoverages
            && columnData[0].lob.data.coverages.vehicleCoverages.length > 0) {
            overrides = tableData.map((tableInfo, index) => {
                const endorsementCoverages = columnData[0].lob.data.coverages.vehicleCoverages[index].coverages.filter(
                    (cov) => {
                        return cov.coverageCategoryCode === 'CAEndorsement_EY';
                    }
                );
                const headerTitle = endorsementCoverages[0].coverageCategoryDisplayName;
                const endorsementCoveragesPublicID = endorsementCoverages.map(
                    (c) => { return { coverageUniqueID: c.publicID }; }
                );


                const coverages = columnData[0].lob.data.coverages.vehicleCoverages[index].coverages.filter(
                    (cov) => {
                        return cov.coverageCategoryCode !== 'CAEndorsement_EY';
                    }
                );
                const coveragesPublicID = coverages.map(
                    (c) => { return { coverageUniqueID: c.publicID }; }
                );
                return {
                    [`quoteTable${index}`]: {
                        data: coveragesPublicID,
                        content: this.getTableColumnContent(columnData, tableInfo)
                    },
                    [`quoteOCPFTable${index}`]: {
                        data: endorsementCoveragesPublicID,
                        content: this.getTableColumnContent(columnData, tableInfo)
                    },
                    [`ocpfAccordion${index}`]: {
                        defaultOpenedId: `ocpfAccordionCard${index}`
                    },
                    [`ocpfAccordionCard${index}`]: {
                        chevronAlignment: 'right',
                        header: headerTitle
                    },
                    [`quoteTableAccordion${index}`]: {
                        defaultOpenedId: `quoteTableAccordionCard${index}`
                    },
                    [`quoteTableAccordionCard${index}`]: {
                        header: tableInfo.header,
                        cardHeadingClassName: 'quoteTableAccordionHeading',
                        chevronAlignment: 'right'
                    },
                    [`scheduledItemsContainer${index}`]: {
                        visible: false
                    }
                };
            });
        } else {
            const packagesLength = _.get(submissionVM, 'lobData.homeowners.offerings.children[0].coverages.dwellingCoverages.children').length;
            overrides = tableData.map((tableInfo, index) => ({
                [`quoteTable${index}`]: {
                    data: tableInfo.data,
                    content: this.getTableColumnContent(columnData, tableInfo)
                },
                [`quoteTable1${index}`]: {
                    data: tableInfo.data,
                    content: this.getTableColumnContent(columnData, tableInfo)
                },
                [`quoteTableAccordion${index}`]: {
                    defaultOpenedId: `quoteTableAccordionCard${index}`
                },
                [`quoteTableAccordionCard${index}`]: {
                    header: tableInfo.header,
                    cardHeadingClassName: 'quoteTableAccordionHeading',
                    chevronAlignment: 'right'
                },
                [`ocpfAccordion${index}`]: {
                    visible: false
                },
                [`scheduledItemsContainer${index}`]: {
                    visible: index < packagesLength,
                }
            }));
        }

        return Object.assign({}, ...overrides);
    }

    generateDesktopOverrides() {
        return {
            ...this.generateDesktopHeaderOverrides(),
            ...this.generateDesktopTableOverrides(),
            ...this.generateScheduledItemOverRide()
        };
    }

    generatePhoneOverrides() {
        return {
            ...this.generatePhoneHeaderOverrides()
        };
    }

    findBlockingPoint(type) {
        const { underwritingIssues } = this.props;
        return underwritingIssues.some((offeringIssue) => {
            if (type === BLOCKS_BIND) {
                return offeringIssue.currentBlockingPoint === BLOCKS_BIND;
            }
            return _.includes(BLOCKS_QUOTES, offeringIssue.currentBlockingPoint);
        });
    }

    visibilityConditionForNoteToUW = () => {
        const { underwritingIssues } = this.props;
        if (underwritingIssues.length === 0
                || (underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === BLOCKS_BIND
                        || uwIssue.currentBlockingPoint === 'BlocksQuote'
                        || uwIssue.currentBlockingPoint === 'BlocksQuoteRelease'
                ).length === 0
                && underwritingIssues.filter(
                    (uwIssue) => uwIssue.currentBlockingPoint === NON_BLOCKING && uwIssue.hasApprovalOrRejection === false
                ).length > 0)) {
            return true;
        }
        return false;
    }

    generateOverrides(breakpoint) {
        const {
            underwritingIssues, quoteID, filterUWIssuesInCustomOffering,
            baseData, bindData, submissionVM
        } = this.props;
        const { formData } = this.state;
        const breakpointOverrides = breakpoint === 'phone'
            ? this.generatePhoneOverrides()
            : this.generateDesktopOverrides();
        return {
            '@field': {
                // apply to all fields
                onValueChange: this.writeValue
            },
            underWritingIssues: {
                underwritingIssues: underwritingIssues,
                blockQuote: this.findBlockingPoint(),
                quoteID: quoteID,
                filterUWIssuesInCustomOffering,
                job: submissionVM.baseData.jobType.value,
                noteForUW: submissionVM.baseData.noteForUW.value
            },
            /* paymentToggle: {
                visible: !this.findBlockingPoint()
            },
            tableContainer: {
                visible: !this.findBlockingPoint()
            },
            tableHeader: {
                visible: !this.findBlockingPoint()
            },*/
            paymentFrequencyDropdownSelect: {
                visible: this.isAutoPolicy(baseData) && baseData.paymentPlanData !== undefined,
                availableValues: baseData.paymentPlanData !== undefined
                    ? this.getPaymentFrequencyData(baseData.paymentPlanData) : [],
                value: formData.paymentFrequency ? formData.paymentFrequency : bindData ? bindData.selectedPaymentPlan : null,
                onValueChange: this.changePaymentPlanFrequency
            },
            autoMultilineDiscountDropdownSelect: {
                visible: this.isAutoPolicy(baseData),
                availableValues: submissionVM.baseData.multiLineApplicableDiscounts.value.map(
                    (item) => {
                        return { code: item, name: item };
                    }
                ),
                // availableValues: this.getAutoMultilineDiscountData(),
                value: formData.autoMultilineDiscount ? formData.autoMultilineDiscount : baseData.autoMultiLineDiscount,
                onValueChange: this.changeAutoMultilineDiscount
            },
            multilineDiscountSelect: {
                visible: !this.isAutoPolicy(baseData),
                availableValues: submissionVM.baseData.multiLineApplicableDiscounts.value.map(
                    (item) => {
                        return { code: item, name: item };
                    }
                ),
                value: formData.autoMultilineDiscount
                    ? formData.autoMultilineDiscount : baseData.autoMultiLineDiscount,
                onValueChange: this.changeAutoMultilineDiscount
            },
            binderPolicy: {
                value: formData.binderPolicyNumber_EY
                    ? formData.binderPolicyNumber_EY : baseData.binderPolicyNumber_EY,
                onValueChange: this.changeBinderPolicy
            },
            uwNoteForUnderwriter: {
                visible: this.visibilityConditionForNoteToUW(),
                value: formData.noteForUW ? formData.noteForUW : baseData.noteForUW,
                onValueChange: this.changenoteForUW
            },
            ...breakpointOverrides
        };
    }

    renderClauseTable = (breakpoint) => {
        const { setComponentValidation, onScheduleChange } = this.props;
        const { formData } = this.state;

        const dataForComponent = {
            ...formData,
            ..._.pick(this.props, ['columnData', 'tableData'])
        };

        const resolvers = {
            resolveClassNameMap: styles,
            resolveComponentMap: {
                chevron: Chevron,
                quotetableaccordioncarditerablecomponent: QuoteTableAccordionCardIterableComponent,
                // underwritingissues: UnderwritingIssues
            },
            resolveCallbackMap: {
                onScheduleChange: onScheduleChange
            }
        };
        const generatedMetadata = selectMetadata(breakpoint);
        return (
            <ViewModelForm
                uiProps={generatedMetadata.componentContent}
                model={dataForComponent}
                overrideProps={this.generateOverrides(breakpoint)}
                onValueChange={this.writeValue}
                onValidationChange={setComponentValidation}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        );
    };

    render() {
        return (
            <BreakpointTrackerContext.Consumer>
                {this.renderClauseTable}
            </BreakpointTrackerContext.Consumer>
        );
    }
}

export default withValidation(QuoteClauseTable);
